import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Loader2,
  RefreshCcw,
  ShoppingBag,
  CheckCircle2,
  Clock,
  IndianRupee,
  Store,
  Wallet,
  Users,
} from 'lucide-react';
import LayoutWithSidebar from '../components/LayoutWithSidebar';
import { fetchDashboardSummary } from '../services/dashboardService';

const numberFormatter = new Intl.NumberFormat('en-IN');

const currencyFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatNumber = (value) => numberFormatter.format(Number(value || 0));

const formatCurrency = (value) => {
  const amount = Number.parseFloat(value);
  if (!Number.isFinite(amount)) {
    return currencyFormatter.format(0);
  }
  return currencyFormatter.format(amount);
};

const formatDateTime = (value) => {
  if (!value) {
    return '—';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '—';
  }

  return parsed.toLocaleString();
};

const formatDateForInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const SummaryCard = ({ title, value, description, icon: Icon, accentClass }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 p-5 flex items-start gap-4">
    <div className={`p-3 rounded-lg text-white ${accentClass}`}>
      <Icon size={22} />
    </div>
    <div>
      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</p>
      <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">{value}</p>
      {description ? (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{description}</p>
      ) : null}
    </div>
  </div>
);

const CompactStatCard = ({ title, value, icon: Icon, accentClass }) => (
  <div className="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-4 py-3">
    <div className={`p-2 rounded-md text-white ${accentClass}`}>
      <Icon size={18} />
    </div>
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{title}</p>
      <p className="text-base font-semibold text-gray-900 dark:text-white">{value}</p>
    </div>
  </div>
);

const SectionCard = ({ title, description, children }) => (
  <section className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-800 rounded-xl shadow-sm">
    <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
      {description ? (
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</p>
      ) : null}
    </div>
    <div className="p-5">{children}</div>
  </section>
);

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState(() => {
    const today = formatDateForInput(new Date());

    return {
      startDate: today,
      endDate: today,
    };
  });
  const [data, setData] = useState({
    summary: {
      todayOrders: 0,
      pendingOrders: 0,
      deliveredOrders: 0,
      totalSalesAmount: 0,
      totalShopSettlementPending: 0,
      pendingDeliveryCollectionAmount: 0,
    },
    deliveryCollections: {
      executives: [],
      totalPendingAmount: 0,
      totalSettledAmount: 0,
      totalCollectionAmount: 0,
      totalExecutives: 0,
    },
    shopSettlements: { shops: [], totalPayableAmount: 0, totalShops: 0 },
  });

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetchDashboardSummary({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });
      const responseData = response?.data?.data || response?.data || {};
      setData({
        summary: {
          todayOrders: responseData?.summary?.todayOrders || 0,
          pendingOrders: responseData?.summary?.pendingOrders || 0,
          deliveredOrders: responseData?.summary?.deliveredOrders || 0,
          totalSalesAmount: responseData?.summary?.totalSalesAmount || 0,
          totalShopSettlementPending:
            responseData?.summary?.totalShopSettlementPending || 0,
          pendingDeliveryCollectionAmount:
            responseData?.summary?.pendingDeliveryCollectionAmount || 0,
        },
        deliveryCollections: {
          totalExecutives: responseData?.deliveryCollections?.totalExecutives || 0,
          totalPendingAmount: responseData?.deliveryCollections?.totalPendingAmount || 0,
          totalSettledAmount: responseData?.deliveryCollections?.totalSettledAmount || 0,
          totalCollectionAmount:
            responseData?.deliveryCollections?.totalCollectionAmount || 0,
          executives: responseData?.deliveryCollections?.executives || [],
        },
        shopSettlements: {
          totalShops: responseData?.shopSettlements?.totalShops || 0,
          totalPayableAmount: responseData?.shopSettlements?.totalPayableAmount || 0,
          shops: responseData?.shopSettlements?.shops || [],
        },
      });
    } catch (err) {
      console.error(err);
      setError('Failed to load dashboard information. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [dateRange.endDate, dateRange.startDate]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handleDateChange = useCallback((key) => {
    return (event) => {
      const value = event.target.value;

      setDateRange((previous) => {
        if (!value) {
          return previous;
        }

        const next = {
          ...previous,
          [key]: value,
        };

        if (key === 'startDate' && value > previous.endDate) {
          next.endDate = value;
        }

        if (key === 'endDate' && value < previous.startDate) {
          next.startDate = value;
        }

        return next;
      });
    };
  }, []);

  const handlePresetRange = useCallback((days) => {
    const end = new Date();
    const start = new Date();

    start.setDate(end.getDate() - (days - 1));

    setDateRange({
      startDate: formatDateForInput(start),
      endDate: formatDateForInput(end),
    });
  }, []);

  const selectedRangeLabel = useMemo(() => {
    const start = new Date(`${dateRange.startDate}T00:00:00`);
    const end = new Date(`${dateRange.endDate}T00:00:00`);

    const formatter = new Intl.DateTimeFormat(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });

    if (dateRange.startDate === dateRange.endDate) {
      return formatter.format(start);
    }

    return `${formatter.format(start)} – ${formatter.format(end)}`;
  }, [dateRange.endDate, dateRange.startDate]);

  const isSingleDayRange = useMemo(
    () => dateRange.startDate === dateRange.endDate,
    [dateRange.endDate, dateRange.startDate]
  );

  const summaryRangeText = useMemo(() => {
    return isSingleDayRange
      ? `on ${selectedRangeLabel}`
      : `during ${selectedRangeLabel}`;
  }, [isSingleDayRange, selectedRangeLabel]);

  const summaryCards = useMemo(
    () => [
      {
        title: 'Orders Created',
        value: formatNumber(data.summary.todayOrders),
        description: `Orders created ${summaryRangeText}.`,
        icon: ShoppingBag,
        accentClass: 'bg-indigo-500',
      },
      {
        title: 'Pending Orders',
        value: formatNumber(data.summary.pendingOrders),
        description: `Orders awaiting fulfilment ${summaryRangeText}.`,
        icon: Clock,
        accentClass: 'bg-amber-500',
      },
      {
        title: 'Orders Delivered',
        value: formatNumber(data.summary.deliveredOrders),
        description: `Delivered orders ${summaryRangeText}.`,
        icon: CheckCircle2,
        accentClass: 'bg-emerald-500',
      },
      {
        title: 'Total Sales (Selected Range)',
        value: formatCurrency(data.summary.totalSalesAmount),
        description: `Delivered order value ${summaryRangeText}.`,
        icon: IndianRupee,
        accentClass: 'bg-sky-500',
      },
      {
        title: 'Pending Shop Settlements',
        value: formatCurrency(data.summary.totalShopSettlementPending),
        description: `Amount to settle with shops ${summaryRangeText}.`,
        icon: Store,
        accentClass: 'bg-rose-500',
      },
      {
        title: 'Pending Delivery Collections',
        value: formatCurrency(data.summary.pendingDeliveryCollectionAmount),
        description: `To collect from delivery executives ${summaryRangeText}.`,
        icon: Wallet,
        accentClass: 'bg-purple-500',
      },
    ],
    [data.summary, summaryRangeText]
  );

  return (
    <LayoutWithSidebar>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Dashboard</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Overview of orders, collections, and settlements for the selected
              date range.
            </p>
          </div>
          <button
            type="button"
            onClick={loadDashboard}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            disabled={loading}
          >
            <RefreshCcw size={18} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>

        <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-800 rounded-xl shadow-sm p-5">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                Filter by date range
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Showing data for {selectedRangeLabel}.
              </p>
            </div>
            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-end">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Start date
                  <input
                    type="date"
                    value={dateRange.startDate}
                    onChange={handleDateChange('startDate')}
                    max={dateRange.endDate}
                    className="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </label>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  End date
                  <input
                    type="date"
                    value={dateRange.endDate}
                    onChange={handleDateChange('endDate')}
                    min={dateRange.startDate}
                    className="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </label>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handlePresetRange(1)}
                  className="inline-flex items-center justify-center rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-900/60 disabled:opacity-60"
                  disabled={loading}
                >
                  Today
                </button>
                <button
                  type="button"
                  onClick={() => handlePresetRange(7)}
                  className="inline-flex items-center justify-center rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-900/60 disabled:opacity-60"
                  disabled={loading}
                >
                  Last 7 days
                </button>
              </div>
            </div>
          </div>
        </div>

        {error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 size={42} className="animate-spin text-indigo-500" />
          </div>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {summaryCards.map((card) => (
                <SummaryCard key={card.title} {...card} />
              ))}
            </div>

            <SectionCard
              title="Delivery Executive Collections"
              description={`Summary of collections and pending settlements ${summaryRangeText}.`}
            >
              <div className="space-y-6">
                <div className="grid gap-4 sm:grid-cols-3">
                  <CompactStatCard
                    title="Total Collection"
                    value={formatCurrency(data.deliveryCollections.totalCollectionAmount)}
                    icon={Wallet}
                    accentClass="bg-indigo-500"
                  />
                  <CompactStatCard
                    title="Pending Collection"
                    value={formatCurrency(data.deliveryCollections.totalPendingAmount)}
                    icon={Clock}
                    accentClass="bg-amber-500"
                  />
                  <CompactStatCard
                    title="Settled Collection"
                    value={formatCurrency(data.deliveryCollections.totalSettledAmount)}
                    icon={CheckCircle2}
                    accentClass="bg-emerald-500"
                  />
                </div>

                {data.deliveryCollections.executives.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No pending collections from delivery executives for the selected
                    date range.
                  </p>
                ) : (
                  <div className="space-y-6">
                    {data.deliveryCollections.executives.map((executive) => (
                      <div
                        key={executive.deliveryBoyId}
                        className="border border-gray-100 dark:border-gray-700 rounded-lg"
                      >
                        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between p-4 bg-gray-50 dark:bg-gray-900/50 rounded-t-lg">
                          <div>
                            <div className="flex items-center gap-2 text-gray-900 dark:text-white">
                              <Users size={18} className="text-indigo-500" />
                              <span className="font-medium">{executive.name}</span>
                            </div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {executive.phone ? `Phone: ${executive.phone}` : 'Phone not available'}
                            </p>
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-300">
                            <div className="font-semibold text-base text-gray-900 dark:text-white">
                              {formatCurrency(executive.totalAmount)}
                            </div>
                            <div className="flex flex-wrap gap-3 mt-1">
                              <span>Cash: {formatCurrency(executive.cashAmount)}</span>
                              <span>UPI: {formatCurrency(executive.upiAmount)}</span>
                              <span>Mixed: {formatCurrency(executive.mixedAmount)}</span>
                            </div>
                            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              {formatNumber(executive.collectionCount)} collections
                            </div>
                          </div>
                        </div>

                        {executive.collections.length > 0 ? (
                          <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                              <thead className="bg-gray-100 dark:bg-gray-900/60 text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                <tr>
                                  <th className="px-4 py-3 font-medium">Order</th>
                                  <th className="px-4 py-3 font-medium">Amount</th>
                                  <th className="px-4 py-3 font-medium">Mode</th>
                                  <th className="px-4 py-3 font-medium">Collected At</th>
                                  <th className="px-4 py-3 font-medium">Reference</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100 dark:divide-gray-800 bg-white dark:bg-gray-900/40">
                                {executive.collections.map((collection) => (
                                  <tr key={collection.id}>
                                    <td className="px-4 py-3 text-gray-700 dark:text-gray-200">
                                      {collection.orderShowId ? `#${collection.orderShowId}` : collection.orderId}
                                    </td>
                                    <td className="px-4 py-3 text-gray-900 dark:text-white font-medium">
                                      {formatCurrency(collection.amount)}
                                    </td>
                                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                                      {collection.mode || '—'}
                                    </td>
                                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                                      {formatDateTime(collection.collectedAt)}
                                    </td>
                                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                                      {collection.referenceNumber || '—'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </SectionCard>

            <SectionCard
              title="Shop Settlements Pending"
              description={`Amount to settle with shops ${summaryRangeText} (${formatCurrency(
                data.shopSettlements.totalPayableAmount
              )})`}
            >
              {data.shopSettlements.shops.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No pending settlements for shops in the selected date range.
                </p>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Showing {formatNumber(data.shopSettlements.shops.length)} shops that
                    still need settlement today. Review the pending amount to be
                    transferred to each shop below.
                  </p>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-100 dark:bg-gray-900/60 text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        <tr>
                          <th className="px-4 py-3 font-medium">Shop</th>
                          <th className="px-4 py-3 font-medium text-right">Pending Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-800 bg-white dark:bg-gray-900/40">
                        {data.shopSettlements.shops.map((shop) => (
                          <React.Fragment key={shop.shopId}>
                            <tr>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2 text-gray-900 dark:text-white">
                                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                                    <Store size={16} />
                                  </span>
                                  <div>
                                    <div className="font-medium leading-snug">{shop.shopName}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-right text-gray-900 dark:text-white font-semibold">
                                {formatCurrency(shop.payableAmount)}
                              </td>
                            </tr>
                            {shop.orders.length > 0 ? (
                              <tr>
                                <td colSpan={2} className="px-4 pb-4">
                                  <details className="group rounded-lg border border-dashed border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 px-4 py-3">
                                    <summary className="flex cursor-pointer items-center gap-2 text-sm font-medium text-indigo-600 outline-none transition hover:text-indigo-500 dark:text-indigo-300 dark:hover:text-indigo-200">
                                      View delivered orders
                                      <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                                        ({formatNumber(shop.orders.length)} order{shop.orders.length > 1 ? 's' : ''})
                                      </span>
                                    </summary>
                                    <div className="mt-3 overflow-x-auto">
                                      <table className="min-w-full text-xs">
                                        <thead className="bg-gray-100 dark:bg-gray-900/60 text-left uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                          <tr>
                                            <th className="px-3 py-2 font-medium">Order</th>
                                            <th className="px-3 py-2 font-medium">Amount</th>
                                            <th className="px-3 py-2 font-medium">Payment Mode</th>
                                            <th className="px-3 py-2 font-medium">Delivered At</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 dark:divide-gray-800 bg-white dark:bg-gray-900/40">
                                          {shop.orders.map((order) => (
                                            <tr key={order.orderShopId}>
                                              <td className="px-3 py-2 text-gray-700 dark:text-gray-200">
                                                {order.orderShowId ? `#${order.orderShowId}` : order.orderId}
                                              </td>
                                              <td className="px-3 py-2 text-gray-900 dark:text-white font-medium">
                                                {formatCurrency(order.finalAmount)}
                                              </td>
                                              <td className="px-3 py-2 text-gray-600 dark:text-gray-300">
                                                {order.paymentMode || '—'}
                                              </td>
                                              <td className="px-3 py-2 text-gray-600 dark:text-gray-300">
                                                {formatDateTime(order.deliveredAt)}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </details>
                                </td>
                              </tr>
                            ) : null}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </SectionCard>
          </>
        )}
      </div>
    </LayoutWithSidebar>
  );
}
